import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID } from "@constants/prefilled-credential";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { useMemo } from "react";
import { PropertiesSectionDescription } from "../properties-section-header/properties-section-description";

type PrefilledCredentialContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    primaryServiceFormattedAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    documentationLinks: Array<AxnDocumentationLink>;
    updateAttributes: (attributes: Array<FormattedRpServiceConfigurationAttribute>) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
};

export const PrefilledCredentialContainer = (props: PrefilledCredentialContainerProps) => {
    const {
        isDisabled,
        properties,
        updateAttributes,
        updateProperties,
        documentationLinks,
        validationErrors,
        primaryServiceFormattedAttributes
    } = props;


    const attributes = useMemo(() => {
        return primaryServiceFormattedAttributes.filter(attribute => {
            return attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE;
        })
    }, [primaryServiceFormattedAttributes]);


    const isPrefilledCredentialEnabled = useMemo(() => {
        return properties.some(property => property.propertyTypeId === POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID && property.addedToService);
    }, [properties]);



    const onChangeEnablePrefilledCredential = (value: string) => {
        const enabled = isStringTrue(value);
        updateProperties([{ propertyTypeId: POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID, newValue: value, added: enabled }]);
    }



    const onChangeEnabledCredentialAttribute = (option: SelectOption) => {
        const attributeTypeId = option?.value;
        const finalAttributes = primaryServiceFormattedAttributes.map((attribute) => {
            if (attribute.status === EntityStatusTypes.ACTIVE) {
                if (attribute.attributeType.id === attributeTypeId) {
                    attribute.populateCredentialFromThisAttribute = 'true';
                } else {
                    attribute.populateCredentialFromThisAttribute = 'false';
                }
            }

            return attribute;
        });

        updateAttributes(finalAttributes);
    }

    const attributeLabelOptions = useMemo(() => {
        return attributes.map(al => ({ label: al.attributeType.name, value: al.attributeType.id }));
    }, [attributes]);


    const selectedAttributeOption = useMemo(() => {
        const foundAttribute = attributes.find(attr => isStringTrue(attr.populateCredentialFromThisAttribute));
        if (foundAttribute) {
            return { label: foundAttribute.attributeType.name, value: foundAttribute.attributeType.id };
        }
        else { return null }
    }, [attributes]);

    const prefilledCredentialResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.prefilledCredentials;

    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === 'credentials-mapping-widget');
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);
    return (

        <div>
            <ContentPageHeader title={prefilledCredentialResources.title} />
            <ContentPageBody isMaxWidth={true}>
            <PropertiesSectionDescription description={prefilledCredentialResources.descriptionText} documentationLink={documentationLink} />
                <RadioInputFormGroup
                    controlId={'enable-prefilled-credential'}
                    automationId={'enable-prefilled-credential'}
                    disabled={isDisabled}
                    isRequired={false}
                    label={prefilledCredentialResources.inputs.enabledPrefilledCredential.label}
                    labelWidth={LabelWidthTypes.LARGE}
                    value={isPrefilledCredentialEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    onChange={onChangeEnablePrefilledCredential}
                    afterLabelTooltipText={prefilledCredentialResources.inputs.enabledPrefilledCredential.tooltipText}
                />
                {isPrefilledCredentialEnabled &&
                    <>
                        <SelectFormGroup
                            controlId={`select-attribute-select-input`}
                            automationId={`select-attribute-select-input`}
                            label={prefilledCredentialResources.inputs.selectAttribute.label}
                            ariaLabel={prefilledCredentialResources.inputs.selectAttribute.label}
                            disabled={isDisabled}
                            isClearable={true}
                            isRequired={true}
                            isSearchable={true}
                            layout={LayoutTypes.ROW}
                            labelWidth={LabelWidthTypes.LARGE}
                            onChange={onChangeEnabledCredentialAttribute}
                            options={attributeLabelOptions}
                            selectedOption={selectedAttributeOption}
                        />
                        {validationErrors.prefilledCredentialAttributeError}
                    </>}
            </ContentPageBody>
        </div>
    );
}